import moment from "moment";
import type {
  AuditDashboardAuditReceiptItem,
  AuditDashboardPosReceiptItem,
  AuditDashboardReceiptsByDateModel,
} from "models";

const AUDIT_FIX_OCR_PATH = "/dashboard/audit/fix-ocr/";
const APP_BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");

export interface AuditDashboardInventoryReceiptView {
  key: string;
  queueId: string;
  title: string;
  caption: string;
}

export interface AuditDashboardReceiptGroups {
  inventoryReceipts: AuditDashboardInventoryReceiptView[];
  salesReceiptsInPeriod: AuditDashboardPosReceiptItem[];
  salesReceiptsAroundPeriod: AuditDashboardPosReceiptItem[];
}

const getAuditFixOcrBaseUrl = (): string | null => {
  if (!APP_BASE_URL || APP_BASE_URL.includes("localhost") || APP_BASE_URL.includes("127.0.0.1")) {
    return "http://localhost:3001";
  }

  if (APP_BASE_URL === "https://lottoreco.dev.sofmen.com/api") {
    return "https://lottoreco-admin.dev.sofmen.com";
  }

  if (APP_BASE_URL === "https://lottoreco.stg.sofmen.com/api") {
    return "https://lottoreco-admin.stg.sofmen.com";
  }

  if (APP_BASE_URL === "https://lottoreco.com/api") {
    return null;
  }

  return "http://localhost:3001";
};

export const buildAuditDashboardReceiptReviewUrl = (
  storeId: string | undefined,
  receiptQueueId: string
): string | undefined => {
  if (!storeId) {
    return "#";
  }

  const baseUrl = getAuditFixOcrBaseUrl();

  if (!baseUrl) {
    return undefined;
  }

  const params = new URLSearchParams({
    aud_status: "finalized",
    aud_page: "1",
    aud_store_id: storeId,
    fx_store_id: storeId,
  });

  return `${baseUrl}${AUDIT_FIX_OCR_PATH}${receiptQueueId}?${params.toString()}`;
};

export const formatAuditDashboardPosReceiptSummary = (receipt: AuditDashboardPosReceiptItem): string => {
  const reportDate = receipt.reportDate ? moment(receipt.reportDate).format("MM/DD") : "date n/a";
  const reportTime = receipt.reportTime ? moment(receipt.reportTime, "HH:mm:ss").format("h:mm a") : "time n/a";
  const location = receipt.locationKey || "Vending";
  const scratcherAmount = Number(receipt.scratcherAmount || 0).toFixed(2);
  const drawGameAmount = Number(receipt.drawGameAmount || 0).toFixed(2);
  const relationLabel =
    receipt.windowRelation === "before_period"
      ? "Before period | "
      : receipt.windowRelation === "after_period"
        ? "After period | "
        : "";

  return `${relationLabel}${location} ${reportDate} @ ${reportTime} | Scratchers ${
    receipt.scratcherCount || 0
  } / $${scratcherAmount} | Draw $${drawGameAmount}`;
};

const getInventoryReceipts = (
  audit: AuditDashboardAuditReceiptItem | null
): AuditDashboardInventoryReceiptView[] => {
  if (!audit) {
    return [];
  }

  const receipts: AuditDashboardInventoryReceiptView[] = [];

  if (audit.previousReceiptAuditQueueId) {
    receipts.push({
      key: `previous-${audit.previousReceiptAuditQueueId}`,
      queueId: audit.previousReceiptAuditQueueId,
      title: "Previous Inventory Receipt",
      caption: audit.previousAuditTime ? moment(audit.previousAuditTime).format("MM/DD @ h:mm a") : "previous audit",
    });
  }

  if (audit.receiptAuditQueueId) {
    receipts.push({
      key: `current-${audit.receiptAuditQueueId}`,
      queueId: audit.receiptAuditQueueId,
      title: "Current Inventory Receipt",
      caption: audit.time ? moment(audit.time).format("MM/DD @ h:mm a") : "current audit",
    });
  }

  return receipts;
};

export const getAuditDashboardReceiptGroups = (
  receiptsByDateModel: AuditDashboardReceiptsByDateModel | null | undefined,
  auditId: string | null | undefined
): AuditDashboardReceiptGroups => {
  const selectedAudit = receiptsByDateModel?.getAuditById(auditId) ?? null;
  const posReceipts = receiptsByDateModel?.getPosReceiptsByAuditId(auditId) ?? [];

  const relationOrder = { before_period: 0, after_period: 1 } as const;

  return {
    inventoryReceipts: getInventoryReceipts(selectedAudit),
    salesReceiptsInPeriod: posReceipts.filter((receipt) => (receipt.windowRelation || "in_period") === "in_period"),
    salesReceiptsAroundPeriod: posReceipts
      .filter((receipt) => receipt.windowRelation === "before_period" || receipt.windowRelation === "after_period")
      .sort(
        (a, b) =>
          relationOrder[a.windowRelation as "before_period" | "after_period"] -
          relationOrder[b.windowRelation as "before_period" | "after_period"]
      ),
  };
};
